import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Trash2, Copy, BookOpen, Settings as SettingsIcon } from "lucide-react";
import { getApiClient } from "../lib/apiClient";
import { blankDefinition } from "../features/designer/editorStore";
import type { Flow } from "../types/flow";
import type { FlowTemplateSummary } from "../types/api";
import { StudioBanner } from "../components/StudioBanner";
import { MerchantMenu } from "../components/MerchantMenu";
import { ThemeToggle } from "../components/ThemeToggle";

export function FlowsIndexPage() {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [templates, setTemplates] = useState<FlowTemplateSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [showTemplates, setShowTemplates] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const client = getApiClient();
      const [flowsList, catalog] = await Promise.all([
        client.flows.list(),
        client.templates.catalog(),
      ]);
      if (!cancelled) {
        setFlows(flowsList);
        setTemplates(catalog.templates);
        setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function createFlow() {
    const client = getApiClient();
    const flow = await client.flows.create({ name: "Untitled flow" });
    const version = await client.versions.create(flow.id, {
      definition: blankDefinition(),
    });
    navigate(`/flows/${flow.id}?v=${version.id}`);
  }

  async function forkTemplate(templateId: string, templateName: string) {
    const flow = await getApiClient().templates.fork({
      template_id: templateId,
      name: templateName,
    });
    navigate(`/flows/${flow.id}`);
  }

  async function deleteFlow(flowId: string) {
    if (!confirm("Delete this flow? Cannot be undone in Studio mode.")) return;
    await getApiClient().flows.delete(flowId);
    setFlows((prev) => prev.filter((f) => f.id !== flowId));
  }

  return (
    <div className="min-h-screen flex flex-col">
      <StudioBanner />
      <header className="border-b border-neutral-200 dark:border-neutral-800 px-6 py-4 flex items-center justify-between">
        <h1 className="text-xl font-semibold">Cost+ Studio</h1>
        <div className="flex items-center gap-2">
          <Link
            to="/sources"
            className="flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900"
          >
            <SettingsIcon className="w-4 h-4" />
            Sources
          </Link>
          <button
            onClick={() => setShowTemplates(true)}
            className="flex items-center gap-1.5 rounded-md border border-neutral-200 dark:border-neutral-700 px-3 py-1.5 text-sm font-medium hover:bg-neutral-50 dark:hover:bg-neutral-900"
          >
            <BookOpen className="w-4 h-4" />
            Start from template
          </button>
          <button
            onClick={createFlow}
            className="flex items-center gap-1.5 rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-3 py-1.5 text-sm font-medium hover:bg-category-trigger/20"
          >
            <Plus className="w-4 h-4" />
            New flow
          </button>
          <ThemeToggle />
          <MerchantMenu />
        </div>
      </header>

      <main className="flex-1 px-6 py-6 max-w-4xl w-full mx-auto">
        <h2 className="text-sm uppercase tracking-wide text-neutral-500 mb-3">Your flows</h2>
        {loading ? (
          <p className="text-neutral-500">Loading…</p>
        ) : flows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-neutral-300 dark:border-neutral-700 p-8 text-center">
            <p className="text-neutral-500 mb-3">
              No flows yet. Create one to open the designer.
            </p>
            <button
              onClick={createFlow}
              className="inline-flex items-center gap-1.5 rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-3 py-1.5 text-sm font-medium hover:bg-category-trigger/20"
            >
              <Plus className="w-4 h-4" />
              Create your first flow
            </button>
          </div>
        ) : (
          <ul className="space-y-2">
            {flows.map((flow) => (
              <li
                key={flow.id}
                className="group rounded-md border border-neutral-200 dark:border-neutral-800 px-4 py-3 flex items-center justify-between hover:bg-neutral-50 dark:hover:bg-neutral-900"
              >
                <Link to={`/flows/${flow.id}`} className="flex-1 min-w-0 mr-4">
                  <p className="font-medium truncate">{flow.name}</p>
                  <p className="text-xs text-neutral-500 font-mono truncate">{flow.id}</p>
                </Link>
                <span className="text-xs text-neutral-500 mr-3">
                  {flow.active_version_id ? "published" : "draft"}
                </span>
                <button
                  onClick={() => deleteFlow(flow.id)}
                  className="opacity-0 group-hover:opacity-100 text-neutral-500 hover:text-category-error transition-opacity"
                  aria-label="Delete flow"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>


      {showTemplates ? (
        <div
          className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-6"
          onClick={() => setShowTemplates(false)}
        >
          <div
            className="max-w-2xl w-full rounded-lg bg-white dark:bg-neutral-950 shadow-xl border border-neutral-200 dark:border-neutral-800"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-3 border-b border-neutral-200 dark:border-neutral-800 flex items-center">
              <div>
                <h3 className="font-semibold text-sm">Start from a template</h3>
                <p className="text-xs text-neutral-500">
                  Curated starter flows. Fork creates a draft under your account.
                </p>
              </div>
              <button
                onClick={() => setShowTemplates(false)}
                className="ml-auto text-neutral-500 hover:text-neutral-900 dark:hover:text-neutral-100"
              >
                Close
              </button>
            </div>
            <ul className="divide-y divide-neutral-200 dark:divide-neutral-800 max-h-[60vh] overflow-y-auto">
              {templates.map((t) => (
                <li key={t.id} className="px-5 py-3 flex items-start gap-3">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{t.name}</p>
                    <p className="text-xs text-neutral-500">{t.description}</p>
                    <p className="text-[11px] text-neutral-500 mt-1">
                      Exercises: {t.exercises.join(", ")}
                    </p>
                  </div>
                  <button
                    onClick={() => forkTemplate(t.id, t.name)}
                    className="flex items-center gap-1.5 rounded-md bg-category-trigger/10 border border-category-trigger/30 text-category-trigger px-3 py-1.5 text-sm font-medium hover:bg-category-trigger/20"
                  >
                    <Copy className="w-4 h-4" />
                    Fork
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  );
}
